import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@redotech/redo-web/components/ui/select";
import { memo } from "react";
import { useUpdateConversationStatus } from "../../../hooks/use-conversation-mutations";

interface EditableStatusCellProps {
  conversationId: ConversationId;
  status: ConversationStatus;
}

function getStatusConfig(status: ConversationStatus): {
  label: string;
  color: RedoBadgeColor;
} {
  switch (status) {
    case "OPEN":
      return { label: "Open", color: "success" };
    case "CLOSED":
      return { label: "Closed", color: "gray" };
    case "SNOOZED":
      return { label: "Snoozed", color: "warning" };
    case "IN_PROGRESS":
      return { label: "In Progress", color: "blue" };
    case "HIDDEN":
      return { label: "Hidden", color: "gray" };
    case "ARCHIVED":
      return { label: "Archived", color: "gray" };
    case "DELETED":
      return { label: "Deleted", color: "error" };
    default:
      return { label: status, color: "gray" };
  }
}

const STATUS_OPTIONS: Array<{
  value: ConversationStatus;
  label: string;
  color: RedoBadgeColor;
}> = [
  { value: "OPEN", label: "Open", color: "success" },
  { value: "IN_PROGRESS", label: "In Progress", color: "blue" },
  { value: "SNOOZED", label: "Snoozed", color: "warning" },
  { value: "CLOSED", label: "Closed", color: "gray" },
  { value: "ARCHIVED", label: "Archived", color: "gray" },
  { value: "HIDDEN", label: "Hidden", color: "gray" },
  { value: "DELETED", label: "Deleted", color: "error" },
];

export const EditableStatusCell = memo(function EditableStatusCell({
  conversationId,
  status,
}: EditableStatusCellProps) {
  const updateStatus = useUpdateConversationStatus();

  const config = getStatusConfig(status);

  const handleValueChange = useHandler((value: string) => {
    const newStatus = value as ConversationStatus;

    updateStatus(conversationId, newStatus);
  });

  return (
    <Select onValueChange={handleValueChange} value={status}>
      <SelectTrigger size="sm">
        <SelectValue>
          <RedoBadge color={config.color} size="sm" text={config.label} />
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <RedoBadge color={option.color} size="sm" text={option.label} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
